import $ from 'jquery';

// Module invariant: $loading.hasClass('hidden') == (messageStack.length == 0)
let $loading: JQuery | null = null;
let $loadingText: JQuery | null = null;
const messageStack: Array<string> = [];

const TRANSITION_MS = 150;
let hideTimeoutId: number | null = null;
let hasInitFaded = false;

function _startHide(): void {
  // On first hide, fade out. On subsequent hides, hide immediately.
  if (hasInitFaded) {
    $loading!.addClass('hidden');
    return;
  }
  $loading!.addClass('transparent');
  hideTimeoutId = window.setTimeout(() => {
    $loading!.addClass('hidden').removeClass('transparent');
    hasInitFaded = true;
  }, TRANSITION_MS);
}

function _stopHide(): void {
  $loading!.removeClass('hidden');
  // If we haven't finished the initial hide, clean up fade.
  if (!hasInitFaded) {
    $loading!.removeClass('transparent');
    clearTimeout(hideTimeoutId!);
  }
}

let hasInitialized = false;

export function init(): void {
  // Makes subsequent calls to init noop.
  if (hasInitialized) {
    return;
  }
  hasInitialized = true;

  $loading = $('#loading');
  $loadingText = $loading.find('.loading-text');
  _startHide();
}

export function showFileLoading(): void {
  $('#fileLoading').removeClass('hidden');
}

export function hideFileLoading(): void {
  $('#fileLoading').addClass('hidden');
}

export function show(message: string | null = null): void {
  if ($loading == null || $loadingText == null) {
    return;
  }
  if (messageStack.length === 0) {
    _stopHide();
  }

  messageStack.push($loadingText.text());
  if (message != null) {
    $loadingText.text(message);
  }
}

export function set(message: string): void {
  if ($loading == null || $loadingText == null) {
    return;
  }
  $loadingText.text(message);
}

export function hide(): void {
  if ($loading == null || $loadingText == null) {
    return;
  }
  if (messageStack.length === 0) {
    // While we wouldn't expect hide() to be called unless there were a messageStack, we silently abort
    // since it's relatively safe to no-op.
    return;
  }
  $loadingText.text(messageStack.pop()!);
  if (messageStack.length === 0) {
    _startHide();
  }
}

export function isHidden(): boolean {
  return !hasInitialized || ($loading != null && $loading.hasClass('hidden'));
}

/**
 * Run a callback with the loading screen temporarily hidden.
 * Useful for showing a modal in the middle of a process,
 * because if the loading screen is not hidden, it will cover and block the modal.
 */
export async function withLoadingScreenHidden<T>(callback: () => Promise<T>): Promise<T> {
  if ($loading == null || $loadingText == null) {
    return await callback();
  }
  if (messageStack.length === 0) {
    // While we wouldn't expect hide() to be called unless there were a messageStack, we silently abort
    // since it's relatively safe to no-op.
    return await callback();
  }

  try {
    _startHide();
    return await callback();
  } finally {
    _stopHide();
  }
}

export function resetLoadingScreenForTests(): void {
  hasInitialized = false;
  $loading = null;
  $loadingText = null;
  messageStack.length = 0;
  hasInitFaded = false;
  clearTimeout(hideTimeoutId!);
  hideTimeoutId = null;
}
